import styled from '@emotion/styled';
import { useLocalizationContext } from '@eventbrite/create-common';
import { VenueFactory } from '@eventbrite/event-create-ui';
import {
    EntryPointProps,
    SalesEligibleBanner,
    SubmitFormProps,
} from '@eventbrite/sales-eligible-onboarding';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppContext } from '../../../AppContext';
import {
    useSavedAutoCreatedEventFormValuesContext,
    useTransientAutoCreatedEventContext,
} from '../../../DraftEventStoreProvider';
import {
    CoreUserWithActiveOrganization,
    FormSection,
    Section,
} from '../../../shared';
import { createTransientEvent } from '../api';
import { AutoCreateEventFormValues } from '../types';
import {
    DEFAULT_END_TIME,
    DEFAULT_START_TIME,
    getDefaultDateWithOffset,
} from '../utils/dateHandlers';
import { ActionBar } from './ActionBar';
import { CreateEventTitle } from './CreateEventTitle';
import { DatesFormField } from './DatesFormField';
import { EventTitleFormField } from './EventTitleFormField';
import { VenueLocation } from './Location';
import { TicketsAvailableFormField } from './TicketsAvailableFormField';
import { TicketsPriceFormField } from './TicketsPriceFormField';

export const EVENT_SOURCE_CREATE = 'coyote';
export const EVENT_SOURCE_AUTO_CREATE = 'auto_create';

export enum CreateEventFormTestId {
    Container = 'AutoCreateEventAppContainer',
}

export interface CreateEventFormProps {
    onSaveStart: () => void;
    onSaveError: () => void;
    onSaveSuccess: () => void;
}

export const CreateEventForm = ({
    onSaveStart,
    onSaveError,
    onSaveSuccess,
}: CreateEventFormProps) => {
    const { timezone, locale, currencySymbol } = useLocalizationContext();
    const { user } = useAppContext();

    const { saveAutoCreatedEventFormValues } =
        useSavedAutoCreatedEventFormValuesContext();
    const { saveTransientAutoCreatedEvent } =
        useTransientAutoCreatedEventContext();

    const { errorSimulation, updateContext } = useAppContext();

    const {
        shouldErrorOnCreateTransientEvent,
        createTransientEventErrorCount,
        createTransientErrorThreshold,
    } = errorSimulation;

    const methods = useForm<AutoCreateEventFormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
        defaultValues: {
            dates: [getDefaultDateWithOffset(), getDefaultDateWithOffset()],
            startTime: DEFAULT_START_TIME,
            endTime: DEFAULT_END_TIME,
            timezone,
            areTicketsFree: false,
            venue: VenueFactory.empty(),
        },
    });

    const saveForm = useCallback(
        async (values: AutoCreateEventFormValues) => {
            try {
                onSaveStart();

                // for use when we are ready to create the event after account creation
                saveAutoCreatedEventFormValues(values);

                const { event } = await createTransientEvent({
                    values,
                    userTimezone: timezone,
                    locale,
                    options: {
                        testingFlags: {
                            shouldError: shouldErrorOnCreateTransientEvent,
                        },
                    },
                });

                if (event) {
                    event.location_type = values.locationType;
                    event.venue = values.venue;
                }

                // to render the event preview page
                saveTransientAutoCreatedEvent(event);

                onSaveSuccess();
            } catch (error) {
                onSaveError();

                // for testing
                if (shouldErrorOnCreateTransientEvent) {
                    let shouldError = true;

                    if (
                        createTransientEventErrorCount + 1 >=
                        createTransientErrorThreshold
                    ) {
                        shouldError = false;
                    }

                    updateContext({
                        errorSimulation: {
                            ...errorSimulation,
                            createTransientEventErrorCount:
                                createTransientEventErrorCount + 1,
                            shouldErrorOnCreateTransientEvent: shouldError,
                        },
                    });
                }
            }
        },
        [
            locale,
            timezone,
            errorSimulation,
            shouldErrorOnCreateTransientEvent,
            createTransientEventErrorCount,
            createTransientErrorThreshold,
            updateContext,
            onSaveError,
            onSaveStart,
            onSaveSuccess,
            saveAutoCreatedEventFormValues,
            saveTransientAutoCreatedEvent,
        ],
    );

    const updateUserContextFromSalesEligible = (formData: SubmitFormProps) => {
        const { firstName, lastName, email, phoneNumber } = formData;
        updateContext({
            shouldSendSalesEligibleEvent: true,
            phoneNumber,
            user: {
                ...user,
                firstName,
                lastName,
                email,
            } as CoreUserWithActiveOrganization,
        });
    };

    return (
        <FormProvider {...methods}>
            <Container>
                <form
                    name="autoCreateEventForm"
                    onSubmit={methods.handleSubmit(async (data) => {
                        await saveForm(data);
                    })}
                    data-testid={CreateEventFormTestId.Container}
                    noValidate
                >
                    <Section spacing={48}>
                        <CreateEventTitle />
                    </Section>
                    <FormSection spacing={48}>
                        <EventTitleFormField />
                    </FormSection>
                    <FormSection spacing={48}>
                        <DatesFormField />
                    </FormSection>
                    <FormSection spacing={48}>
                        <VenueLocation />
                    </FormSection>
                    <FormSection spacing={48}>
                        <TicketsPriceFormField
                            currencySymbol={currencySymbol}
                        />
                    </FormSection>
                    <FormSection spacing={0}>
                        <TicketsAvailableFormField />
                    </FormSection>

                    <ActionBar />
                </form>
                <SalesEligibleBanner
                    entryPoint={EntryPointProps.LoggedOutEventCreate}
                    updateUserContextFromSalesEligible={
                        updateUserContextFromSalesEligible
                    }
                />
            </Container>
        </FormProvider>
    );
};

const Container = styled.div`
    padding-bottom: 40px;
`;
